import { useMemo, useState } from "react";
import { Check, ChevronDown, ListFilter } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/command";

export type ShowPickerValue = string | number;

export interface ShowPickerOption<T extends ShowPickerValue> {
  value: T;
  label: string;
  /** Secondary line, also searchable (e.g. part number, customer). */
  description?: string;
  /** Options sharing a group are listed under one heading (e.g. shop). */
  group?: string;
}

interface ShowPickerProps<T extends ShowPickerValue> {
  /** Plural noun for the button and empty state, e.g. "jobs", "technicians". */
  noun: string;
  options: ShowPickerOption<T>[];
  /** Empty selection means everything is shown. */
  selected: T[];
  onChange: (selected: T[]) => void;
}

/**
 * Toolbar picker that narrows which jobs / technicians / machines the month calendar shows.
 * The month grid merges every row into one calendar, so selection lives here instead of in a
 * row-label column that would look like the Day/Week rows but behave differently.
 * Changes apply immediately; there is no separate confirm step.
 */
export default function ShowPicker<T extends ShowPickerValue>({
  noun,
  options,
  selected,
  onChange
}: ShowPickerProps<T>) {
  const [open, setOpen] = useState(false);
  const selectedSet = useMemo(() => new Set<ShowPickerValue>(selected), [selected]);

  const groups = useMemo(() => {
    const byGroup = new Map<string, ShowPickerOption<T>[]>();
    for (const option of options) {
      const key = option.group ?? "";
      if (!byGroup.has(key)) byGroup.set(key, []);
      byGroup.get(key)!.push(option);
    }
    return Array.from(byGroup.entries());
  }, [options]);

  // Selections can point at rows that a later search/filter removed; only count visible ones.
  const activeCount = options.filter((o) => selectedSet.has(o.value)).length;
  const buttonLabel =
    activeCount === 0 ? `All ${noun}` : `${activeCount} of ${options.length} ${noun}`;

  const toggle = (value: T) => {
    onChange(
      selectedSet.has(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
    );
  };

  const toggleGroup = (groupOptions: ShowPickerOption<T>[]) => {
    const values = groupOptions.map((o) => o.value);
    const allSelected = values.every((v) => selectedSet.has(v));
    onChange(
      allSelected
        ? selected.filter((v) => !values.includes(v))
        : Array.from(new Set([...selected, ...values]))
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "aero:flex aero:items-center aero:gap-2 aero:h-9 aero:px-3 aero:rounded-md aero:border aero:text-sm aero:transition-colors",
            activeCount > 0
              ? "aero:border-brand-primary-600 aero:bg-brand-primary-50 aero:text-brand-primary-700"
              : "aero:border-gray-300 aero:bg-white aero:text-neutral-900 aero:hover:bg-gray-50"
          )}
          aria-label={`Choose ${noun} to show`}
        >
          <ListFilter className="aero:h-4 aero:w-4" />
          <span className="aero:text-neutral-500">Show:</span>
          <span className="aero:font-medium">{buttonLabel}</span>
          <ChevronDown className="aero:h-4 aero:w-4 aero:opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="aero:w-80 aero:p-0">
        <Command>
          <CommandInput placeholder={`Search ${noun}...`} />
          <div className="aero:flex aero:items-center aero:justify-between aero:px-3 aero:py-2 aero:border-b aero:border-neutral-200 aero:text-xs">
            <button
              type="button"
              className="aero:font-medium aero:text-brand-primary-600 aero:hover:underline"
              onClick={() => onChange(options.map((o) => o.value))}
            >
              Select all
            </button>
            <button
              type="button"
              className="aero:font-medium aero:text-neutral-500 aero:hover:underline aero:disabled:opacity-40"
              disabled={selected.length === 0}
              onClick={() => onChange([])}
            >
              Clear (show all)
            </button>
          </div>
          <CommandList className="aero:max-h-80">
            <CommandEmpty>No {noun} found.</CommandEmpty>
            {groups.map(([group, groupOptions]) => (
              <CommandGroup
                key={group || "__ungrouped"}
                heading={
                  group ? (
                    <button
                      type="button"
                      className="aero:w-full aero:text-left aero:hover:text-brand-primary-600"
                      onClick={() => toggleGroup(groupOptions)}
                      title={`Toggle all in ${group}`}
                    >
                      {group}
                    </button>
                  ) : undefined
                }
              >
                {groupOptions.map((option) => {
                  const isSelected = selectedSet.has(option.value);
                  return (
                    <CommandItem
                      key={String(option.value)}
                      value={`${option.label} ${option.description ?? ""} ${String(option.value)}`}
                      onSelect={() => toggle(option.value)}
                      className="aero:flex aero:items-start aero:gap-2"
                    >
                      <span
                        className={cn(
                          "aero:mt-0.5 aero:flex aero:h-4 aero:w-4 aero:shrink-0 aero:items-center aero:justify-center aero:rounded-sm aero:border",
                          isSelected
                            ? "aero:border-brand-primary-600 aero:bg-brand-primary-600"
                            : "aero:border-neutral-400 aero:bg-white"
                        )}
                      >
                        {isSelected && <Check className="aero:h-3 aero:w-3 aero:text-white" />}
                      </span>
                      <span className="aero:flex aero:min-w-0 aero:flex-col">
                        <span className="aero:truncate aero:text-sm">{option.label}</span>
                        {option.description && (
                          <span className="aero:truncate aero:text-xs aero:text-muted-foreground">
                            {option.description}
                          </span>
                        )}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
